import { useEffect, useState } from 'react'
import { Plus, X, Mail, RefreshCw, Trash2, Edit2, Check, AlertCircle, Users, Copy, ExternalLink, Smartphone, Download, FileText } from 'lucide-react'
import api from '../../services/api'
import { useAuthStore } from '../../stores/authStore'

const ROLES = [
  { value: 'tenant_admin', label: 'Admin' },
  { value: 'corp_safety', label: 'Corp Safety' },
  { value: 'ops_safety', label: 'Ops Safety' },
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'field_worker', label: 'Field Worker' },
  { value: 'contractor_supervisor', label: 'Contractor Supervisor' },
  { value: 'contractor_standard', label: 'Contractor' },
  { value: 'read_only', label: 'Read Only' },
]

const ROLE_BADGE: Record<string, string> = {
  tenant_admin: 'badge-critical',
  corp_safety: 'badge-high',
  ops_safety: 'badge-high',
  supervisor: 'badge-medium',
  field_worker: 'badge-low',
  contractor_supervisor: 'badge-medium',
  contractor_standard: 'badge-low',
  read_only: '',
}

function roleLabel(role: string) {
  return ROLES.find(r => r.value === role)?.label || role
}

export default function UsersSettings() {
  const { accessToken } = useAuthStore()
  const [users, setUsers] = useState<any[]>([])
  const [invitations, setInvitations] = useState<any[]>([])
  const [sites, setSites] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [showInvite, setShowInvite] = useState(false)
  const [showExportMenu, setShowExportMenu] = useState(false)
  const [exporting, setExporting] = useState<string | null>(null)
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'field_worker', site_id: '' })
  const [inviting, setInviting] = useState(false)
  const [inviteError, setInviteError] = useState('')
  const [inviteLink, setInviteLink] = useState('')
  const [linkCopied, setLinkCopied] = useState(false)
  const [editUser, setEditUser] = useState<any>(null)
  const [editForm, setEditForm] = useState({ role: '', site_id: '', mobile: '', active: true })
  const [saving, setSaving] = useState(false)
  const [toast, setToast] = useState('')

  const [sendingAppLink, setSendingAppLink] = useState<string | null>(null)

  const showToast = (msg: string) => {
    setToast(msg)
    setTimeout(() => setToast(''), 3000)
  }

  const handleSendAppLink = async (userId: string, name: string) => {
    setSendingAppLink(userId)
    try {
      await api.post(`/users/${userId}/send-app-link`)
      showToast(`App download link sent to ${name}`)
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Failed to send SMS')
    } finally { setSendingAppLink(null) }
  }

  const load = async () => {
    setLoading(true)
    try {
      const [u, inv, s] = await Promise.all([
        api.get('/users'),
        api.get('/users/invitations'),
        api.get('/organisation/sites'),
      ])
      setUsers(u.data || [])
      setInvitations(inv.data || [])
      setSites(s.data || [])
    } catch {}
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!inviteForm.email) { setInviteError('Email is required'); return }
    setInviting(true)
    setInviteError('')
    try {
      const { data } = await api.post('/users/invite', {
        email: inviteForm.email,
        role: inviteForm.role,
        site_id: inviteForm.site_id || undefined,
      })
      setInviteLink(data.invite_link || '')
      load()
    } catch (err: any) {
      setInviteError(err.response?.data?.error || 'Failed to send invitation')
    }
    setInviting(false)
  }

  const handleResend = async (id: string) => {
    try {
      await api.post(`/users/invite/${id}/resend`)
      showToast('Invitation resent')
      load()
    } catch {}
  }

  const handleCancelInvite = async (id: string) => {
    if (!confirm('Cancel this invitation?')) return
    try {
      await api.delete(`/users/invite/${id}`)
      showToast('Invitation cancelled')
      load()
    } catch {}
  }

  const openEdit = (user: any) => {
    setEditUser(user)
    setEditForm({ role: user.role, site_id: user.site_id || '', mobile: user.mobile || '', active: user.active === 1 || user.active === true })
  }

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    try {
      await api.patch(`/users/${editUser.id}`, {
        role: editForm.role,
        site_id: editForm.site_id || null,
        mobile: editForm.mobile || null,
        active: editForm.active ? 1 : 0,
      })
      setEditUser(null)
      showToast('User updated')
      load()
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to update')
    }
    setSaving(false)
  }

  const downloadExport = async (format: 'csv' | 'xlsx') => {
    setExporting(format)
    setShowExportMenu(false)
    try {
      const resp = await fetch(`/api/users/export/${format}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      if (!resp.ok) throw new Error()
      const blob = await resp.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `users-${new Date().toISOString().slice(0, 10)}.${format}`
      a.click()
      URL.revokeObjectURL(url)
    } catch { alert('Export failed. Try again.') }
    setExporting(null)
  }

  if (loading) return <div className="text-center py-12" style={{ color: 'var(--text-muted)' }}>Loading...</div>

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>User Management</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>{users.length} users · {invitations.length} pending invitations</p>
        </div>
        <div className="flex items-center gap-2">
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowExportMenu(m => !m)}
              disabled={!!exporting}
              className="btn btn-secondary text-sm flex items-center gap-1.5"
            >
              <Download size={13} />
              {exporting ? `Exporting…` : 'Export'}
            </button>
            {showExportMenu && (
              <div
                style={{
                  position: 'absolute', top: '100%', right: 0, marginTop: 4,
                  background: 'var(--surface)', border: '1px solid var(--border)',
                  borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                  minWidth: 190, zIndex: 50,
                }}
                onMouseLeave={() => setShowExportMenu(false)}
              >
                <button
                  onClick={() => downloadExport('csv')}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--text)' }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                >
                  <FileText size={14} style={{ color: 'var(--green)' }} /> CSV (All Users)
                </button>
                <div style={{ height: 1, background: 'var(--border)', margin: '0 8px' }} />
                <button
                  onClick={() => downloadExport('xlsx')}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--text)' }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                >
                  <FileText size={14} style={{ color: 'var(--blue)' }} /> Excel (Active + Offboarded)
                </button>
              </div>
            )}
          </div>
          <button onClick={() => setShowInvite(true)} className="btn btn-primary"><Plus size={16} /> Invite User</button>
        </div>
      </div>

      {/* Active users */}
      <div className="card p-0 overflow-hidden">
        <div className="px-4 py-3 border-b flex items-center gap-2" style={{ borderColor: 'var(--border)' }}>
          <Users size={16} style={{ color: 'var(--text-secondary)' }} />
          <span className="font-semibold text-sm" style={{ color: 'var(--text)' }}>Active Users</span>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg)' }}>
              <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Name</th>
              <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Company</th>
              <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Email</th>
              <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Role</th>
              <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Site</th>
              <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Mobile</th>
              <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Last Login</th>
              <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Status</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} style={{ borderBottom: '1px solid var(--border)' }}>
                <td className="px-4 py-3 font-medium" style={{ color: 'var(--text)' }}>{u.first_name} {u.last_name}</td>
                <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-secondary)' }}>
                  {u.company_name
                    ? <span className="px-2 py-0.5 rounded text-xs font-medium" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>{u.company_name}</span>
                    : <span style={{ color: 'var(--text-muted)' }}>Own staff</span>
                  }
                </td>
                <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-secondary)' }}>{u.email}</td>
                <td className="px-4 py-3">
                  <span className={`badge ${ROLE_BADGE[u.role] || ''}`}>{roleLabel(u.role)}</span>
                </td>
                <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-secondary)' }}>{u.site_name || '—'}</td>
                <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-secondary)' }}>{u.mobile || '—'}</td>
                <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-muted)' }}>
                  {u.last_login ? new Date(u.last_login).toLocaleDateString('en-AU') : 'Never'}
                </td>
                <td className="px-4 py-3">
                  <span className={`badge ${u.active ? 'badge-low' : ''}`} style={!u.active ? { background: 'var(--bg)', color: 'var(--text-muted)' } : {}}>
                    {u.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-1.5 items-center">
                    <button onClick={() => openEdit(u)} className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: 12 }}>
                      <Edit2 size={13} /> Edit
                    </button>
                    {u.mobile && (
                      <button
                        onClick={() => handleSendAppLink(u.id, u.first_name)}
                        disabled={sendingAppLink === u.id}
                        title={`Send app download link to ${u.mobile}`}
                        className="btn btn-secondary"
                        style={{ padding: '4px 8px', fontSize: 12 }}
                      >
                        <Smartphone size={13} />
                        {sendingAppLink === u.id ? '…' : 'Send App'}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pending invitations */}
      {invitations.length > 0 && (
        <div className="card p-0 overflow-hidden">
          <div className="px-4 py-3 border-b flex items-center gap-2" style={{ borderColor: 'var(--border)' }}>
            <Mail size={16} style={{ color: 'var(--text-secondary)' }} />
            <span className="font-semibold text-sm" style={{ color: 'var(--text)' }}>Pending Invitations</span>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg)' }}>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Email</th>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Role</th>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Site</th>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Invited By</th>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Expires</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {invitations.map(inv => (
                <tr key={inv.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td className="px-4 py-3 font-medium" style={{ color: 'var(--text)' }}>{inv.email}</td>
                  <td className="px-4 py-3">
                    <span className={`badge ${ROLE_BADGE[inv.role] || ''}`}>{roleLabel(inv.role)}</span>
                  </td>
                  <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-secondary)' }}>{inv.site_name || '—'}</td>
                  <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-secondary)' }}>{inv.invited_by_name}</td>
                  <td className="px-4 py-3 text-xs" style={{ color: new Date(inv.expires_at) < new Date() ? 'var(--red)' : 'var(--text-muted)' }}>
                    {new Date(inv.expires_at).toLocaleDateString('en-AU')}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button onClick={() => handleResend(inv.id)} className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: 12 }}>
                        <RefreshCw size={13} /> Resend
                      </button>
                      <button onClick={() => handleCancelInvite(inv.id)} className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: 12, color: 'var(--red)' }}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Invite modal */}
      {showInvite && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="w-full max-w-md rounded-2xl shadow-xl" style={{ background: 'var(--white)' }}>
            <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'var(--border)' }}>
              <h2 className="font-semibold text-lg" style={{ color: 'var(--text)' }}>Invite User</h2>
              <button onClick={() => { setShowInvite(false); setInviteError(''); setInviteLink('') }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={20} /></button>
            </div>

            {inviteLink ? (
              <div className="px-6 py-5 space-y-4">
                <div className="p-3 rounded-xl text-sm" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
                  <p className="font-semibold mb-1" style={{ color: 'var(--text)' }}>Invitation created</p>
                  <p className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>
                    An email was sent if delivery is configured. You can also share this link directly:
                  </p>
                  <div className="flex items-center gap-2">
                    <input readOnly value={inviteLink} className="flex-1 text-xs" style={{ color: 'var(--text-secondary)', background: 'var(--bg)' }} />
                    <button
                      type="button"
                      className="btn btn-secondary"
                      style={{ padding: '4px 10px', fontSize: 12, flexShrink: 0 }}
                      onClick={() => { navigator.clipboard.writeText(inviteLink); setLinkCopied(true); setTimeout(() => setLinkCopied(false), 2000) }}
                    >
                      {linkCopied ? <Check size={13} /> : <Copy size={13} />}
                      {linkCopied ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                </div>
                <div className="flex gap-3">
                  <button className="btn btn-primary" onClick={() => { setInviteLink(''); setInviteForm({ email: '', role: 'field_worker', site_id: '' }) }}>
                    Invite another
                  </button>
                  <button className="btn btn-secondary" onClick={() => { setShowInvite(false); setInviteLink(''); showToast('Invitation created') }}>
                    Done
                  </button>
                </div>
              </div>
            ) : (
            <form onSubmit={handleInvite} className="px-6 py-5 space-y-4">
              <div>
                <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Email Address *</label>
                <input type="email" value={inviteForm.email} onChange={e => setInviteForm(f => ({ ...f, email: e.target.value }))} placeholder="user@company.com.au" required />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Role</label>
                  <select value={inviteForm.role} onChange={e => setInviteForm(f => ({ ...f, role: e.target.value }))}>
                    {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Site (optional)</label>
                  <select value={inviteForm.site_id} onChange={e => setInviteForm(f => ({ ...f, site_id: e.target.value }))}>
                    <option value="">— No site —</option>
                    {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
              </div>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                An invitation email will be sent if email delivery is configured. The invite link will be shown here so you can share it directly.
              </p>
              {inviteError && (
                <div className="flex items-start gap-2 text-sm p-3 rounded-lg" style={{ background: 'var(--red-light)', color: 'var(--red)' }}>
                  <AlertCircle size={15} className="flex-shrink-0 mt-0.5" />{inviteError}
                </div>
              )}
              <div className="flex gap-3 pt-1">
                <button type="submit" className="btn btn-primary" disabled={inviting}>{inviting ? 'Sending...' : 'Send Invitation'}</button>
                <button type="button" className="btn btn-secondary" onClick={() => { setShowInvite(false); setInviteError('') }}>Cancel</button>
              </div>
            </form>
            )}
          </div>
        </div>
      )}

      {/* Edit user modal */}
      {editUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="w-full max-w-md rounded-2xl shadow-xl" style={{ background: 'var(--white)' }}>
            <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'var(--border)' }}>
              <h2 className="font-semibold text-lg" style={{ color: 'var(--text)' }}>Edit User — {editUser.first_name} {editUser.last_name}</h2>
              <button onClick={() => setEditUser(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={20} /></button>
            </div>
            <form onSubmit={handleEdit} className="px-6 py-5 space-y-4">
              <div>
                <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Role</label>
                <select value={editForm.role} onChange={e => setEditForm(f => ({ ...f, role: e.target.value }))}>
                  {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Site</label>
                <select value={editForm.site_id} onChange={e => setEditForm(f => ({ ...f, site_id: e.target.value }))}>
                  <option value="">— No site —</option>
                  {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Mobile (for SMS notifications)</label>
                <input type="tel" value={editForm.mobile} onChange={e => setEditForm(f => ({ ...f, mobile: e.target.value }))} placeholder="+61400000000" />
              </div>
              <div className="flex items-center gap-3">
                <input type="checkbox" id="active-toggle" checked={editForm.active} onChange={e => setEditForm(f => ({ ...f, active: e.target.checked }))} style={{ width: 16, height: 16 }} />
                <label htmlFor="active-toggle" className="text-sm" style={{ color: 'var(--text-secondary)' }}>Account active</label>
              </div>
              <div className="flex gap-3 pt-1">
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
                <button type="button" className="btn btn-secondary" onClick={() => setEditUser(null)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-sm font-medium text-white" style={{ background: 'var(--green, #16a34a)', zIndex: 9999 }}>
          <Check size={16} />{toast}
        </div>
      )}
    </div>
  )
}
